import { Anchor, Badge, Box, Button, Divider, Group, Modal, Stack, Text } from '@mantine/core';
import { IconCheck, IconDna, IconExternalLink } from '@tabler/icons-react';
import type { NutritionVariant, Zygosity, ZygosityDescriptions } from '@mdev/common';

interface VariantDetailDialogProps {
  variant: NutritionVariant | null;
  zygDesc?: ZygosityDescriptions;
  isOpen: boolean;
  onClose: () => void;
}

const ZYG_COLOR: Record<Zygosity, string> = {
  '+/+': 'red',
  '+/-': 'yellow',
  '-/-': 'green',
  missing: 'gray',
};

export function VariantDetailDialog({
  variant,
  zygDesc,
  isOpen,
  onClose,
}: VariantDetailDialogProps) {
  if (!variant) return null;

  const zygosityStates: Array<{
    key: string;
    zygosity: Zygosity;
    label: string;
    description: string;
    isUserState: boolean;
  }> = [
    {
      key: 'hom_ref',
      zygosity: '-/-',
      label: '−/−',
      description: zygDesc?.hom_ref || 'Homozygous reference (wild-type)',
      isUserState: variant.zygosity === '-/-',
    },
    {
      key: 'het',
      zygosity: '+/-',
      label: '+/−',
      description: zygDesc?.het || 'Heterozygous (one variant allele)',
      isUserState: variant.zygosity === '+/-',
    },
    {
      key: 'hom_alt',
      zygosity: '+/+',
      label: '+/+',
      description: zygDesc?.hom_alt || 'Homozygous variant (two variant alleles)',
      isUserState: variant.zygosity === '+/+',
    },
    ...(variant.gene === 'MAOA' && zygDesc?.hemi
      ? [
          {
            key: 'hemi',
            zygosity: '+/-' as Zygosity,
            label: '+ (hemi)',
            description: zygDesc.hemi,
            isUserState: variant.zygosity === '+/-' && variant.genotype.length === 1,
          },
        ]
      : []),
    {
      key: 'missing',
      zygosity: 'missing',
      label: 'Missing',
      description: zygDesc?.missing || 'Variant not found in your genetic data',
      isUserState: variant.zygosity === 'missing',
    },
  ];

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconDna size={20} />
          <div>
            <Text fw={600}>
              {variant.gene} {variant.variantName}
            </Text>
            <Text size="sm" c="dimmed">
              {variant.rsid}
            </Text>
          </div>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Box
          p="sm"
          style={{
            backgroundColor: 'var(--mantine-color-blue-0)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="sm">
              Your Genotype
            </Text>
            {variant.proxyUsed && (
              <Badge size="xs" color="yellow" variant="light">
                via {variant.proxyUsed}
              </Badge>
            )}
          </Group>
          <Group gap="sm">
            <Text ff="monospace" fw={600}>
              {variant.genotype}
            </Text>
            <Text>→</Text>
            <Badge color={ZYG_COLOR[variant.zygosity]} variant="light">
              {variant.zygosity}
            </Badge>
          </Group>
        </Box>

        <Divider label="All Possible States" />

        <Stack gap="sm">
          {zygosityStates.map((state) => (
            <Box
              key={state.key}
              p="sm"
              style={{
                backgroundColor: state.isUserState
                  ? 'var(--mantine-color-green-0)'
                  : 'var(--mantine-color-gray-0)',
                borderRadius: 'var(--mantine-radius-sm)',
                border: state.isUserState
                  ? '2px solid var(--mantine-color-green-6)'
                  : '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <Group gap="sm" align="flex-start">
                <Badge
                  color={ZYG_COLOR[state.zygosity]}
                  variant={state.isUserState ? 'filled' : 'light'}
                  style={{ minWidth: '60px', textAlign: 'center' }}
                >
                  {state.label}
                </Badge>
                {state.isUserState && (
                  <IconCheck size={16} color="var(--mantine-color-green-6)" />
                )}
                <Text size="sm" style={{ flex: 1 }}>
                  {state.description}
                </Text>
              </Group>
            </Box>
          ))}
        </Stack>

        {variant.functionalImpact && (
          <>
            <Divider label="Functional Impact" />
            <Text size="sm" c="dimmed">
              {variant.functionalImpact}
            </Text>
          </>
        )}

        {variant.references && variant.references.length > 0 && (
          <>
            <Divider label="Primary Research" />
            <Stack gap="xs">
              {variant.references.map((ref) => (
                <Anchor key={ref.url} href={ref.url} target="_blank" size="sm">
                  <Group gap="xs">
                    <IconExternalLink size={14} />
                    <Text>{ref.title || `PubMed ${ref.pmid || 'Reference'}`}</Text>
                  </Group>
                </Anchor>
              ))}
            </Stack>
          </>
        )}

        {variant.notes && (
          <>
            <Divider label="Notes" />
            <Text size="sm" c="dimmed">
              {variant.notes}
            </Text>
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
